import React from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import styles from './GebiedsanalyseModule.module.css';
import { BedrijvenOverpassData, VoorzieningItem } from '../../types';
import { BEDRIJVEN_OVERPASS_ICON_MAP, PinIcon } from './CategoryIcons';

const CATEGORY_META: Record<string, { label: string; color: string }> = {
  detailhandel: { label: 'Detailhandel', color: '#f59e0b' },
  horeca: { label: 'Horeca', color: '#f97316' },
  industrie: { label: 'Industrie', color: '#6b7280' },
  ambacht: { label: 'Ambacht', color: '#a855f7' },
  energie: { label: 'Energie', color: '#eab308' },
  logistiek: { label: 'Logistiek', color: '#8b5cf6' },
  zorg: { label: 'Zorg', color: '#ef4444' },
  dienstverlening: { label: 'Dienstverlening', color: '#3b82f6' },
  agrarisch: { label: 'Agrarisch', color: '#22c55e' },
};

interface BedrijvenOverpassSectionProps {
  data: BedrijvenOverpassData | null;
  onItemClick?: (lat: number, lon: number) => void;
}

export default function BedrijvenOverpassSection({ data, onItemClick }: BedrijvenOverpassSectionProps) {
  if (!data || data.totaal === 0) return null;

  const chartData = data.stats.map(s => ({
    name: CATEGORY_META[s.category]?.label || s.category,
    aantal: s.count,
    fill: CATEGORY_META[s.category]?.color || '#9DA6A8'
  })).sort((a, b) => b.aantal - a.aantal);

  const topBedrijven = data.items
    .filter(i => i.name && i.name !== i.subcategory)
    .slice(0, 5);

  return (
    <div className={styles.section}>
      <h3>Bedrijven & Economie (binnen {data.searchRadius / 1000} km)</h3>

      <div className={styles.bedrijvenOverview}>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{data.totaal}</span>
          <span className={styles.statLabel}>Totaal bedrijven</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{data.stats.length}</span>
          <span className={styles.statLabel}>Categorieen</span>
        </div>
      </div>

      {chartData.length > 0 && (
        <div className={styles.chartContainer}>
          <ResponsiveContainer width="100%" height={Math.max(120, chartData.length * 28 + 20)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 10, top: 4, bottom: 4 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [v, 'Aantal']} />
              <Bar dataKey="aantal" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className={styles.bedrijvenCategoryList}>
        {data.stats.map(stat => {
          const meta = CATEGORY_META[stat.category] || { label: stat.category, color: '#9DA6A8' };
          const IconComp = BEDRIJVEN_OVERPASS_ICON_MAP[stat.category] || PinIcon;
          return (
            <div key={stat.category} className={styles.bedrijvenCategoryItem}>
              <span className={styles.categoryIcon}><IconComp size={16} color={meta.color} /></span>
              <span className={styles.categoryName}>{meta.label}</span>
              <span className={styles.categoryCount}>{stat.count}</span>
            </div>
          );
        })}
      </div>

      {topBedrijven.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <h4 style={{
            fontFamily: "'Open Sans', sans-serif",
            fontSize: 12,
            fontWeight: 600,
            color: '#9DA6A8',
            textTransform: 'uppercase',
            margin: '0 0 6px',
            letterSpacing: '0.5px'
          }}>Top bedrijven</h4>
          <div className={styles.topSectorenList}>
            {topBedrijven.map((item: VoorzieningItem) => {
              const IconComp = BEDRIJVEN_OVERPASS_ICON_MAP[item.category] || PinIcon;
              return (
                <button
                  key={item.osmId}
                  className={styles.omgevingsListItem}
                  onClick={() => onItemClick?.(item.lat, item.lon)}
                >
                  <span className={styles.categoryIcon}>
                    <IconComp size={14} color="#9DA6A8" />
                  </span>
                  <span className={styles.omgevingsListContent}>
                    <span className={styles.omgevingsListTitle}>{item.name}</span>
                    <span className={styles.omgevingsListMeta}>
                      {item.subcategory} — {item.distance} km
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
